/**
 * 「送金依頼を送信」ボタン + 確認ダイアログ (2026-10-26)。
 *
 * 商談ボードのヘッダー右側と、経理 > 送金依頼 に置く。経理部署 (dept = accounting) のアカウントには出さない。
 * 弊社経理への連絡で、メーカーへ送る送金完了通知ではない。
 *
 * 送金依頼中 (requested) すべてをカテゴリーごとに 1 通にまとめ、
 * 営業・通知設定 > 自動通知「現在の送金依頼を経理に連絡するとき」がオンのチャネルへ送る。
 * 送れたカテゴリーの依頼のうち、まだ「送金依頼済み」でないものにラベルが付く。
 */
import { Loader2, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/hooks/useAuth';
import {
  useSendTransferRequestsToAccounting,
  type AccountingChannelOutcome,
  type AccountingNotifyOutcome,
  type TransferBatchGroup,
  type TransferBatchResult,
} from '@/hooks/useTransferRequests';
import { paths } from '@/routes/paths';

const OUTCOME_LABEL: Record<AccountingChannelOutcome, string> = {
  sent: '送信済み',
  failed: '失敗',
  no_target: '送信先なし',
  off: 'オフ',
};

function outcomeSummary(
  o: AccountingNotifyOutcome,
  channels: TransferBatchResult['channels'],
): string {
  const parts: string[] = [];
  if (channels.mail) parts.push(`メール ${OUTCOME_LABEL[o.mail]}`);
  if (channels.googleChat) parts.push(`GC ${OUTCOME_LABEL[o.googleChat]}`);
  if (channels.chatwork) parts.push(`CW ${OUTCOME_LABEL[o.chatwork]}`);
  return parts.join(' / ');
}

function groupLabel(g: TransferBatchGroup): string {
  return g.categoryName ? `${g.categoryName}社` : 'カテゴリー未設定';
}

export function SendTransferRequestsButton({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  // 経理部署のアカウントには出さない (経理へ送るボタンのため。2026-10-26)
  if (user?.dept === 'accounting') return null;
  return (
    <>
      <Button
        variant="secondary"
        size={size}
        onClick={() => setOpen(true)}
        title="送金依頼中のものを経理担当へまとめて連絡します（メーカー宛ではありません）"
      >
        <Send className="size-3.5" />
        送金依頼を送信
      </Button>
      {open && <SendTransferRequestsDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function SendTransferRequestsDialog({ onClose }: { onClose: () => void }) {
  const mutation = useSendTransferRequestsToAccounting();
  // 送信中の表示は自前の state で持つ (確認用の取得と送信で同じ mutation を使うため)
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState<TransferBatchResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const startedRef = useRef(false);

  // 開いたら送らずに文面と送信先を取得
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    mutation
      .mutateAsync({ dryRun: true })
      .then(setPreview)
      .catch((err) => setLoadError((err as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const channels = preview?.channels;
  const allOff = !!channels && !channels.mail && !channels.googleChat && !channels.chatwork;
  const groups = preview?.groups ?? [];
  const canSend = !!preview && !allOff && groups.length > 0 && !sending;

  // プレビューに出す文面: CW がオンなら Chatwork、次に GC、どちらもオフならメール本文
  const previewText = (g: TransferBatchGroup): string =>
    channels?.chatwork ? g.text.chatwork : channels?.googleChat ? g.text.googleChat : g.text.plain;

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await mutation.mutateAsync({ dryRun: false });
      const lines = r.groups.map(
        (g) => `${groupLabel(g)}: ${g.outcome ? outcomeSummary(g.outcome, r.channels) : '-'}`,
      );
      const allSent = r.groups.every(
        (g) =>
          g.outcome &&
          (!r.channels.mail || g.outcome.mail === 'sent') &&
          (!r.channels.googleChat || g.outcome.googleChat === 'sent') &&
          (!r.channels.chatwork || g.outcome.chatwork === 'sent'),
      );
      if (r.markedCount > 0) lines.push(`「送金依頼済み」を付けた件数: ${r.markedCount} 件`);
      const description = <div className="whitespace-pre-line">{lines.join('\n')}</div>;
      if (allSent) {
        toast.success('経理へ送金依頼を送信しました', { description });
      } else {
        toast.warning('一部送信できませんでした', { description, duration: 10000 });
      }
      onClose();
    } catch (err) {
      toast.error(`送信失敗: ${(err as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const targetLine = (on: boolean | undefined, label: string, target: string | null) => (
    <li className="flex gap-2">
      <span className="w-20 shrink-0 text-[var(--text-muted)]">{label}</span>
      {!on ? (
        <span className="text-[var(--text-muted)]">オフ</span>
      ) : target ? (
        <span className="font-medium">{target}</span>
      ) : (
        <span className="text-[var(--accent-amber)]">送信先が未設定のため送られません</span>
      )}
    </li>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>送金依頼を送信（経理宛）</DialogTitle>
        </DialogHeader>

        {loadError ? (
          <p className="text-sm text-[var(--accent-red)]">内容の取得に失敗しました: {loadError}</p>
        ) : !preview ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            送金依頼を読み込み中…
          </div>
        ) : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
              送金依頼中のものすべてを、カテゴリーごとに 1
              通にまとめて経理担当へ送ります。メーカーへの送金完了通知ではありません。
              まだ「送金依頼済み」が付いていないものには、送信後にラベルが付きます。
            </p>

            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
              <p className="text-[11px] font-semibold text-[var(--text-secondary)] mb-1.5">
                送信先
              </p>
              <ul className="text-xs space-y-1">
                {targetLine(channels?.mail, 'メール', preview.targets.email || null)}
                {targetLine(channels?.googleChat, 'Google Chat', preview.targets.googleChatName)}
                {targetLine(channels?.chatwork, 'Chatwork', preview.targets.chatworkName)}
              </ul>
              {allOff && (
                <p className="text-xs text-[var(--accent-amber)] mt-2">
                  「現在の送金依頼を経理に連絡するとき」の通知がすべてオフです。
                  <Link
                    to={`${paths.salesConfigHub}?hub=auto-mail`}
                    className="underline ml-1"
                    onClick={onClose}
                  >
                    営業・通知設定 &gt; 自動通知
                  </Link>
                  で設定してください。
                </p>
              )}
            </div>

            {groups.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">送金依頼中のものはありません。</p>
            ) : (
              groups.map((g) => (
                <div key={g.categoryId ?? '__none__'} className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{groupLabel(g)}</span>
                    <span className="text-xs text-[var(--text-muted)]">
                      {g.count} 件（うち未連絡 {g.newCount} 件）・合計 {g.totalLabel}
                    </span>
                  </div>
                  <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed font-mono bg-[var(--bg-surface)] border border-[var(--border)] rounded p-2 max-h-64 overflow-y-auto">
                    {previewText(g)}
                  </pre>
                </div>
              ))
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={sending}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleSend} disabled={!canSend}>
            <Send className="size-3.5" />
            {sending ? '送信中…' : `送信（${groups.length} 通）`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
